"use client";

import React, { useRef, useState, useEffect } from "react";
import { motion, useAnimationFrame } from "framer-motion";

interface MovingBorderWrapperProps {
  children: React.ReactNode;
  duration?: number;
  borderRadius?: string;
  className?: string;
}

export function MovingBorderWrapper({
  children,
  duration = 3000,
  borderRadius = "0.5rem",
  className = "",
}: MovingBorderWrapperProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const pathRef = useRef<SVGRectElement>(null);
  const [totalLength, setTotalLength] = useState(0);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [rxPixels, setRxPixels] = useState(8);
  
  // Single runner position (unified head+tail)
  const [runnerPos, setRunnerPos] = useState({ x: 0, y: 0, angle: 0 });

  // Get real border-radius in pixels from computed style or convert from string
  useEffect(() => {
    if (!containerRef.current) return;
    
    // Try to get computed border-radius from the first child (the button)
    const firstChild = containerRef.current.querySelector('button, a, [class*="rounded"]');
    if (firstChild) {
      const computed = getComputedStyle(firstChild);
      const computedRadius = parseFloat(computed.borderRadius);
      if (!isNaN(computedRadius) && computedRadius > 0) {
        setRxPixels(computedRadius);
        return;
      }
    }
    
    // Fallback: convert borderRadius prop to pixels
    if (borderRadius.endsWith('rem')) {
      const rem = parseFloat(borderRadius);
      const rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize);
      setRxPixels(rem * rootFontSize);
    } else if (borderRadius.endsWith('px')) {
      setRxPixels(parseFloat(borderRadius));
    } else {
      setRxPixels(parseFloat(borderRadius) || 8);
    }
  }, [borderRadius]);

  // Measure path and dimensions
  useEffect(() => {
    const updateMeasurements = () => {
      if (pathRef.current && containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setDimensions({ width: rect.width, height: rect.height });
        setTotalLength(pathRef.current.getTotalLength());
      }
    };

    // Small delay to ensure rxPixels is set before measuring
    const timer = setTimeout(updateMeasurements, 10);

    const resizeObserver = new ResizeObserver(updateMeasurements);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      clearTimeout(timer);
      resizeObserver.disconnect();
    };
  }, [rxPixels]);

  // Animation loop with proper angle interpolation at wrap point
  useAnimationFrame((time) => {
    if (totalLength === 0 || !pathRef.current) return;

    const progress = (time / duration) % 1;
    const headPosition = progress * totalLength;

    // Get current point
    const headPt = pathRef.current.getPointAtLength(headPosition);
    
    // Calculate angle with proper wrap handling
    // Use 2 points for interpolation when near the end
    const delta = 2;
    const lookAhead1 = (headPosition + delta) % totalLength;
    const nextPt1 = pathRef.current.getPointAtLength(lookAhead1);
    
    let angle: number;
    
    // Check if we're wrapping around (near end of path)
    const distanceToEnd = totalLength - headPosition;
    if (distanceToEnd < delta) {
      // We're wrapping: interpolate between "just before end" and "just after start"
      const ptBeforeEnd = pathRef.current.getPointAtLength(totalLength - 0.5);
      const ptAfterStart = pathRef.current.getPointAtLength(0.5);
      
      // Weight based on how close we are to the wrap point
      const wrapProgress = distanceToEnd / delta;
      
      // Angle from current to before-end
      const angle1 = Math.atan2(ptBeforeEnd.y - headPt.y, ptBeforeEnd.x - headPt.x);
      // Angle from current to after-start (conceptually continuing the loop)
      const angle2 = Math.atan2(ptAfterStart.y - headPt.y, ptAfterStart.x - headPt.x);
      
      // Interpolate angles properly (handling the -180/180 wrap)
      const diff = angle2 - angle1;
      const normalizedDiff = ((diff + Math.PI * 3) % (Math.PI * 2)) - Math.PI;
      angle = (angle1 + normalizedDiff * (1 - wrapProgress)) * (180 / Math.PI);
    } else {
      // Normal case: just look ahead
      angle = Math.atan2(nextPt1.y - headPt.y, nextPt1.x - headPt.x) * (180 / Math.PI);
    }

    setRunnerPos({ x: headPt.x, y: headPt.y, angle });
  });

  return (
    <div ref={containerRef} className={`relative inline-block ${className}`}>
      {/* Hidden SVG for path measurement only */}
      <svg
        className="absolute inset-0 w-full h-full pointer-events-none"
        style={{ overflow: "visible", opacity: 0 }}
        viewBox={`0 0 ${dimensions.width || 100} ${dimensions.height || 100}`}
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        <rect
          ref={pathRef}
          x={0}
          y={0}
          width={dimensions.width || 100}
          height={dimensions.height || 100}
          rx={rxPixels}
          ry={rxPixels}
          fill="none"
          stroke="none"
        />
      </svg>

      {/* SINGLE RUNNER: one line with gradient tail built-in */}
      {totalLength > 0 && (
        <div
          className="absolute pointer-events-none"
          style={{
            left: runnerPos.x,
            top: runnerPos.y,
            width: 28,
            height: 1.5,
            borderRadius: 999,
            background: "linear-gradient(90deg, transparent 0%, rgba(241,249,250,0.3) 40%, rgba(241,249,250,0.7) 70%, #f1f9fa 100%)",
            transform: `translate(-100%, -50%) rotate(${runnerPos.angle}deg)`,
            transformOrigin: "right center",
          }}
        />
      )}

      {children}
    </div>
  );
}
